import {
  Button,
  breakpoints,
  Chip,
  Col,
  Field,
  Icon,
  Input,
  Row,
  SkeletonRows,
  Stack,
  Text,
  type TextStyle,
  useTheme,
} from "@rally/ui-kit";
import { useEffect, useState } from "react";
import { roleLabel } from "../localization/labels.ts";
import { say } from "../localization/say.ts";
import { act, read } from "../net/rally.ts";
import { why } from "../net/why.ts";
import { FaceYouCanChange } from "./a-face-you-can-change.tsx";

type WhoYouAre = {
  handle: string;
  displayName: string | null;
  avatar: string | null;
  role: string | null;
  joined: string | null;
  invitedBy: string | null;
  conversations: number;
  projects: number;
};

export function Profile({ onChanged }: { onChanged: () => void }) {
  const theme = useTheme();
  const narrow = theme.isAtMost(breakpoints.phone);
  const [who, setWho] = useState<WhoYouAre | null>(null);
  const [name, setName] = useState("");
  const [wrong, setWrong] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    read<WhoYouAre>("me", {})
      .then((said) => {
        setWho(said);
        setName(said.displayName ?? "");
      })
      .catch((cause) => setWrong(why(cause, "add")));
  }, []);

  const dirty = who !== null && name.trim() !== (who.displayName ?? "");

  async function keepName() {
    if (!who || name.trim() === (who.displayName ?? "")) return;
    setWrong(null);
    try {
      await act("profile.setName", { name: name.trim() });
      setWho({ ...who, displayName: name.trim() || null });
      onChanged();

      setSaved(true);
      setTimeout(() => setSaved(false), 1600);
    } catch (cause) {
      setWrong(why(cause, "add"));
    }
  }

  function wears(avatar: string | null): void {
    setWho((was) => (was ? { ...was, avatar } : was));
    onChanged();
  }

  if (!who) return <SkeletonRows rows={4} face={64} />;

  return (
    <Stack gap={6}>
      <Col
        style={{
          flexDirection: narrow ? "column" : "row",
          alignItems: narrow ? "stretch" : "center",
          gap: theme.space[5],
        }}
      >
        <Col style={{ alignItems: narrow ? "center" : "stretch" }}>
          <FaceYouCanChange
            name={who.displayName ?? who.handle}
            avatar={who.avatar}
            onChanged={wears}
            onWrong={setWrong}
          />
        </Col>

        <Stack gap={2} style={{ flex: 1, width: "100%" }}>
          <Field label={say("you.displayName")}>
            <Row gap={2} align="center">
              <Input
                value={name}
                onChangeText={setName}
                onBlur={keepName}
                onSubmitEditing={keepName}
                placeholder={who.handle}
                placeholderTextColor={theme.color["text.tertiary"]}
                accessibilityLabel={say("you.displayName")}
                testID="display-name"
                style={{ flex: 1 } as unknown as TextStyle}
              />
              {saved ? (
                <Icon name="check" size={16} color="accent" />
              ) : dirty ? (
                <Button size="sm" variant="soft" onPress={() => void keepName()} testID="display-name-save">
                  {say("you.save")}
                </Button>
              ) : null}
            </Row>
          </Field>
          <Row gap={2} align="center" wrap>
            <Text typeName="body" color="text.secondary">
              @{who.handle}
            </Text>
            {who.role ? <Chip size="sm">{roleLabel(who.role)}</Chip> : null}
          </Row>
        </Stack>
      </Col>

      {wrong ? (
        <Text typeName="caption" color="danger" accessibilityLiveRegion="polite">
          {wrong}
        </Text>
      ) : null}
    </Stack>
  );
}
